/**
 * Task service — reads against Medplum. The inbox is read-only (ENG-3764 D29): no user edits a Task; Tasks open and
 * close only from the records they track.
 */

import type { MedplumClient } from '@medplum/core';
import type { Task } from '@medplum/fhirtypes';
import {
  CHIME_WAITING_ROOM_TASK_CODE,
  TASK_LIFECYCLE_SYSTEM,
  TASK_LIFECYCLE_TIMED_RELEASE_CODE,
  TASK_TYPE_SYSTEM,
} from '@unify/fhir-model/constants';
import { isChimeWaitingRoomTask } from '@unify/fhir-model/resources/chime-waiting-room';

// ─── Fetch ─────────────────────────────────────────────────────────────

export interface SearchTasksParams {
  /** Practitioner reference for owner filter (e.g. "Practitioner/abc"). */
  ownerRef?: string;
  /** Maximum results to return. */
  count?: number;
}

/**
 * Fetch tasks visible to the current user.
 *
 * Held Tasks (`on-hold` + `timed-release`, not yet released by `task-timer`) are excluded in the query so they cannot
 * fill the bounded window: one search for every other status, one for plain `on-hold` Tasks. `_tag:not` alone would
 * also drop released Tasks, which keep the tag. The rest is filtered client-side. Chime waiting-room Tasks are left out
 * in the query too: they are rewritten on every guest poll, so they would otherwise fill the newest-first window.
 */
export async function searchTasks(
  medplum: MedplumClient,
  params: SearchTasksParams = {},
): Promise<Task[]> {
  medplum.invalidateSearches('Task');

  const count = params.count ?? 200;
  const search: Record<string, string> = {
    _sort: '-_lastUpdated',
    _count: String(count),
    'code:not': `${TASK_TYPE_SYSTEM}|${CHIME_WAITING_ROOM_TASK_CODE}`,
  };

  if (params.ownerRef) {
    search.owner = params.ownerRef;
  }

  const [notHeld, plainOnHold] = await Promise.all([
    medplum.searchResources('Task', { ...search, 'status:not': 'on-hold' }),
    medplum.searchResources('Task', {
      ...search,
      status: 'on-hold',
      '_tag:not': `${TASK_LIFECYCLE_SYSTEM}|${TASK_LIFECYCLE_TIMED_RELEASE_CODE}`,
    }),
  ]);
  const lastUpdated = (task: Task): string => task.meta?.lastUpdated ?? '';
  return [...notHeld, ...plainOnHold]
    .filter((task) => !isChimeWaitingRoomTask(task))
    .sort((a, b) => lastUpdated(b).localeCompare(lastUpdated(a)))
    .slice(0, count);
}
